import { io, Socket } from 'socket.io-client';
import { ApiRoutes } from '../routes/routeConstants/apiRoutes';
import { LocalStorageKeys } from '../enums/localStorageKeys';

/**
 * Shared Socket.IO client for real-time job status (preview, layer publish,
 * exports, downloads). Connects to the API origin (same host as REST) with JWT auth.
 *
 * This is a process-wide singleton with several independent consumers, so:
 * - never call `socket.off(event)` without a handler (that kills other consumers'
 *   listeners) — use the unsubscribe returned by `onSocketEvent`;
 * - only `disconnectSocket()` on logout, never on component unmount.
 */
let socket: Socket | null = null;

function apiOrigin(): string {
  const configured = import.meta.env.VITE_APP_API_SOCKET_URL;
  const base = configured || ApiRoutes.API_BASE_URL || '';
  try {
    // Strip trailing /api or path so we hit the HTTP server root where Socket.IO lives.
    const u = new URL(base, window.location.origin);
    return u.origin;
  } catch {
    return window.location.origin;
  }
}

export function getSocket(): Socket {
  if (socket?.connected) return socket;

  const token = localStorage.getItem(LocalStorageKeys.AUTH_TOKEN) || '';
  if (!socket) {
    socket = io(apiOrigin(), {
      autoConnect: false,
      withCredentials: true,
      transports: ['websocket', 'polling'],
      auth: { token },
    });
  } else {
    socket.auth = { token };
  }

  if (!socket.connected) {
    socket.connect();
  }
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

/**
 * Listen for an event. Returns an unsubscribe that removes ONLY this handler,
 * so consumers can't tear down each other's listeners.
 */
export function onSocketEvent<T = unknown>(event: string, handler: (payload: T) => void): () => void {
  const s = getSocket();
  s.on(event, handler as (...args: unknown[]) => void);
  return () => {
    s.off(event, handler as (...args: unknown[]) => void);
  };
}

/**
 * Join a server-side room, and re-join on every (re)connect — socket.io rooms
 * are per-connection, so a reconnect silently drops membership otherwise.
 * Returns a teardown that leaves the room and stops re-subscribing.
 */
export function subscribeRoom(subscribeEvent: string, payload: Record<string, unknown>, unsubscribeEvent: string): () => void {
  const s = getSocket();
  const emitSubscribe = () => s.emit(subscribeEvent, payload);

  emitSubscribe();
  s.on('connect', emitSubscribe);

  return () => {
    s.off('connect', emitSubscribe);
    try {
      s.emit(unsubscribeEvent, payload);
    } catch {
      /* socket already gone */
    }
  };
}

/**
 * Resolve as soon as an event arrives carrying the given jobId. Used to
 * short-circuit a status poll's sleep, so the loop wakes the instant the
 * backend reports the job is done rather than on the next tick.
 */
export function waitForJobEvent<T extends { jobId?: string }>(event: string, jobId: string): { promise: Promise<T | null>; cancel: () => void } {
  let off: (() => void) | null = null;
  let settle: ((value: T | null) => void) | null = null;

  const promise = new Promise<T | null>((resolve) => {
    settle = resolve;
    off = onSocketEvent<T>(event, (payload) => {
      if (String(payload?.jobId) !== String(jobId)) return;
      off?.();
      off = null;
      resolve(payload);
    });
  });

  return {
    promise,
    cancel: () => {
      off?.();
      off = null;
      // Release the race's pending promise so it can't leak.
      settle?.(null);
    },
  };
}
